// pages/admin/DatabasePage.tsx
import React, { useState } from "react";
import { 
  Database, 
  Download, 
  Upload, 
  FileSpreadsheet, 
  FileText, 
  BookOpen,
  Users,
  Phone,
  Ticket,
  Loader2,
  AlertCircle,
  File,
} from "lucide-react";
import { alertSuccess, alertError } from "../../../../helpers/alertError";
import { confirmModal } from "../../../../helpers/confirmModal";

type ExportType = 'calls' | 'tickets' | 'clients' | 'knowledge' | 'all';
type ImportType = 'knowledge' | 'clients' | 'tickets';

export const DatabasePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('all');
  const [importType, setImportType] = useState<ImportType>('knowledge');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importProgress, setImportProgress] = useState(0);

  // Export handlers
  const handleExport = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      alertSuccess(`Export ${exportType} réussi !`);
    } catch (error) {
      alertError("Erreur lors de l'export");
    } finally {
      setLoading(false);
    }
  };

  // Import handlers
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alertError("Veuillez sélectionner un fichier");
      return;
    }

    const confirmed = await confirmModal(
      `Importer ${selectedFile.name} comme ${importType} ?`
    );

    if (confirmed) {
      setLoading(true);
      setImportProgress(0);
      try {
        // Simulation d'import progressif
        for (let i = 0; i <= 100; i += 10) {
          setImportProgress(i);
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        alertSuccess(`Import ${importType} réussi !`);
        setSelectedFile(null);
        setImportProgress(0);
      } catch (error) {
        alertError("Erreur lors de l'import");
      } finally {
        setLoading(false);
      }
    }
  };

  const exportOptions = [
    { value: 'all', label: 'Toutes les données', icon: Database },
    { value: 'calls', label: 'Appels', icon: Phone },
    { value: 'tickets', label: 'Tickets', icon: Ticket },
    { value: 'clients', label: 'Clients', icon: Users },
    { value: 'knowledge', label: 'Base de connaissances', icon: BookOpen },
  ];

  const importOptions = [
    { value: 'knowledge', label: 'Base de connaissances', icon: BookOpen, formats: 'CSV, JSON, Excel' },
    { value: 'clients', label: 'Clients', icon: Users, formats: 'CSV, Excel' },
    { value: 'tickets', label: 'Tickets', icon: Ticket, formats: 'CSV, Excel' },
  ];

  const getImportTemplate = () => {
    const templates = {
      knowledge: 'title, category, description, keywords, agent_procedure, client_response',
      clients: 'firstname, lastname, email, phone, country',
      tickets: 'title, description, priority, source, client_email'
    };
    return templates[importType] || '';
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 select-none animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-100 pb-5 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
            <Database className="w-6 h-6 text-primary" />
            Gestion des données
          </h1>
          <p className="text-sm text-slate-500">
            Exportez, importez et gérez les données de votre plateforme.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* EXPORT SECTION */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-primary/5">
            <div className="flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              <h3 className="text-base font-bold text-slate-800">Exporter les données</h3>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Type d'export</label>
              <div className="grid grid-cols-2 gap-2">
                {exportOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = exportType === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setExportType(option.value as ExportType)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-primary text-white shadow-md shadow-primary/20'
                          : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleExport}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 font-medium text-sm shadow-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                {loading ? 'Export en cours...' : `Exporter en CSV`}
              </button>
              <button
                onClick={handleExport}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-all disabled:opacity-50 font-medium text-sm shadow-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {loading ? 'Export en cours...' : `Exporter en Excel`}
              </button>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500">
                <span className="font-semibold">Format :</span> CSV et Excel supportés.
                Les fichiers seront téléchargés automatiquement.
              </p>
            </div>
          </div>
        </div>

        {/* IMPORT SECTION */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-blue-50/50">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" />
              <h3 className="text-base font-bold text-slate-800">Importer des données</h3>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Type d'import</label>
              <div className="grid grid-cols-3 gap-2">
                {importOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = importType === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setImportType(option.value as ImportType)}
                      className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20'
                          : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Fichier à importer</label>
              <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                selectedFile ? 'border-primary/40 bg-primary/5' : 'border-slate-200 hover:border-blue-400'
              }`}>
                {selectedFile ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <File className="w-8 h-8 text-primary" />
                      <div className="text-left">
                        <p className="text-sm font-medium text-slate-700">{selectedFile.name}</p>
                        <p className="text-xs text-slate-400">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-rose-500 hover:text-rose-700 font-semibold"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.json"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-slate-400" />
                      <p className="text-sm font-medium text-slate-600">
                        Cliquez pour sélectionner un fichier
                      </p>
                      <p className="text-xs text-slate-400">
                        CSV, Excel ou JSON ({importOptions.find(o => o.value === importType)?.formats})
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>

            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500">
                <span className="font-semibold">Modèle :</span>{" "}
                <code className="bg-slate-200 px-1 py-0.5 rounded text-xs">
                  {getImportTemplate()}
                </code>
              </p>
            </div>

            {importProgress > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-slate-600">
                  <span>Import en cours...</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={loading || !selectedFile}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 font-medium text-sm"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {loading ? 'Import en cours...' : `Importer ${selectedFile ? selectedFile.name : 'le fichier'}`}
            </button>
          </div>
        </div>
      </div>

      {/* Info supplémentaire */}
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700">À propos des imports</p>
            <p className="text-xs text-slate-500 mt-1">
              Les imports sont asynchrones. Vous serez notifié par email une fois l'import terminé.
              Les doublons sont automatiquement détectés et ignorés.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};